import type { ReactNode } from 'react'
import type { IndexDef, LevelCode } from '@/api/types/readiness'
import { BANDS, avgLevel, movesToNextBand, scoreBand, type Band } from '@/lib/readiness/score'
import { InfoTooltip, TipBody } from '@/components/readiness/Term'
import { useReadinessStore } from '@/stores/readinessStore'

// ── Readiness score: interpretation and bands (Iteration 5 §6) ──────────────
// One shared tooltip explains the scale, the formula, the band ladder and the
// organisation's position. Bands are derived from the score by `scoreBand`,
// never stored. The band word is plain text in the band colour; hovering or
// focusing it shows that band's one-line tooltip (§6.3).

const WORDS = ['no', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten']
const countWord = (n: number) => WORDS[n] ?? String(n)

export function BandWord({ band, className = '', style, testId }: { band: Band; className?: string; style?: React.CSSProperties; testId?: string }) {
  return (
    <InfoTooltip plain width={280} content={<TipBody title={`${band.min}–${band.max} · ${band.label}`}><p>{band.tooltip}</p></TipBody>}>
      <span className={`rd-band ${className}`} style={{ color: band.token, ...style }} data-testid={testId}>{band.label}</span>
    </InfoTooltip>
  )
}

export function ScoreTooltipBody({ index, levels, orgName, score }: { index: IndexDef; levels: LevelCode[]; orgName: string; score: number }) {
  const n = levels.length || index.domains.reduce((a, d) => a + d.objectives.length, 0)
  const band = scoreBand(score)
  const avg = avgLevel(levels)
  const next = movesToNextBand(levels)
  const org = orgName || 'The organisation'
  return (
    <div className="space-y-2 text-left text-[12px] leading-snug" data-testid="score-tooltip">
      <p>
        <b style={{ color: 'var(--rd-text-primary)' }}>Readiness score · 0 to 100</b>
        <br />
        Higher is better. 100 means every objective has reached L3 Operational, the QRI's top level.
      </p>
      <p>
        <b style={{ color: 'var(--rd-text-primary)' }}>How it's calculated:</b> the average level across all {n} objectives, as a percentage of L3. Each level is worth about 33 points (L1 ≈ 33 · L2 ≈ 67 · L3 = 100). All objectives count equally.
      </p>
      <ul className="space-y-0.5">
        {BANDS.map((b) => {
          const current = b.key === band.key
          return (
            <li key={b.key} className="flex flex-wrap items-center gap-x-2 rounded px-1.5 py-0.5" style={current ? { background: 'var(--rd-tab-active)' } : undefined} data-testid={current ? 'score-tooltip-current-band' : undefined}>
              <span className="rd-mono rd-id" style={{ color: 'var(--rd-text-muted)' }}>{b.min}–{b.max}</span>
              <span className="rd-id font-semibold" style={{ color: b.token }}>· {b.label}</span>
              <span className="min-w-0" style={{ color: 'var(--rd-text-muted)' }}>· {b.short}</span>
              {current && <span className="rd-id ml-auto font-semibold" style={{ color: 'var(--rd-text-primary)' }}>◀ {org} · {score}</span>}
            </li>
          )
        })}
      </ul>
      <p>
        <b style={{ color: 'var(--rd-text-primary)' }}>{org} at {score}:</b> average level {avg.toFixed(1)} of 3.{' '}
        {next.band ? <>{countWord(next.moves)} more level {next.moves === 1 ? 'move' : 'moves'}, on any objectives, reach {next.band.label}.</> : <>{countWord(next.moves)} more level {next.moves === 1 ? 'move' : 'moves'} reach 100, every objective at L3.</>}
      </p>
      <p>The band describes the average, not each objective. The live score includes suggested levels; a saved assessment records confirmed levels only.</p>
      <p style={{ color: 'var(--rd-text-muted)' }}>A Quantizant summary for leadership, not part of the CSA QRI, which rates each objective on its own. The score can dip when Discovery finds more assets; that's better visibility, not regression.</p>
    </div>
  )
}

// Trigger wrapper: ≈360px popover on hover and keyboard focus, closes on
// Escape, `role="tooltip"`, no actions (§6.4, §17).
export function ReadinessScoreTooltip({ index, levels, orgName, score, surface, orgId, children, plain = true }: { index: IndexDef; levels: LevelCode[]; orgName: string; score: number; surface: 'dashboard' | 'progress' | 'report' | 'rail'; orgId: string; children: ReactNode; plain?: boolean }) {
  const track = useReadinessStore((s) => s.track)
  return (
    <InfoTooltip plain={plain} width={360} testId="readiness-score-tooltip" onOpen={() => track(orgId, index.id, { name: 'readiness.score_tooltip_viewed', surface })} content={<ScoreTooltipBody index={index} levels={levels} orgName={orgName} score={score} />}>
      {children}
    </InfoTooltip>
  )
}
